// Purpose: Modal primitive. Backdrop + dialog + header/body/footer
// slots. Escape closes; backdrop click closes; focus returns to trigger
// on close. Entry animation: fade + 0.98→1 scale (200ms); respects
// prefers-reduced-motion.
//
// Direct replacement for the existing components/Modal.tsx (Phase 6
// minimal implementation) — same API surface (open, onClose, title,
// children, footer) plus size prop.

import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { XIcon } from '../icons/index.tsx';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  /** Max width. Default 'md' (560px). */
  size?: 'sm' | 'md' | 'lg';
}

const SIZES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-[440px]',
  md: 'max-w-[560px]',
  lg: 'max-w-[720px]',
};

export function Modal({ open, onClose, title, children, footer, size = 'md' }: ModalProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      className={[
        'fixed inset-0 z-50',
        'flex items-center justify-center p-4',
        'bg-[var(--overlay)] backdrop-blur-[8px]',
        'animate-[ast-fade-in_var(--duration-base)_var(--ease-out)]',
      ].join(' ')}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className={[
          'w-full bg-surface-elevated border border-border-subtle rounded-lg shadow-xl',
          'animate-[ast-scale-in_var(--duration-base)_var(--ease-out)]',
          SIZES[size],
        ].join(' ')}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-border-default">
          <h2 className="text-heading-m text-fg-primary">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-secondary hover:bg-bg-tertiary hover:text-fg-primary transition-colors duration-fast ease-out"
          >
            <XIcon size={16} />
          </button>
        </div>
        <div className="px-5 py-4 text-body-m text-fg-secondary">{children}</div>
        {footer ? (
          <div className="flex items-center justify-end gap-2 border-t border-border-default px-5 py-3">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
